import { Graphics, Sprite, Text, Texture } from "pixi.js"
import { ScreenDimensions, ScreenMessage, TileSize } from "game-common/models"
import { TextRenderer } from "../text/text_renderer"
import { Fader } from "./fader"
import { buildAmbientStripTexture } from "./ambient_strip"

// The "hero" banner: a big center-screen announcement. Same calm ambient look as the
// "important" strip — a full-width translucent band whose left/right edges fade to nothing,
// gently faded in and out — but with the giant text and centered placement that make it a
// "hero" moment rather than a routine notice.

const VERTICAL_PADDING = 30 // space above/below the text inside the strip
const STRIP_ALPHA = 0.4 // center opacity of the strip; kept airy so the giant text carries it
const VERTICAL_OFFSET = 55 // nudge down to sit visually centered given the surrounding UI

export class HeroMessage extends Graphics {
    heroText: Text
    background: Sprite | null = null
    fadeInFader: Fader | null = null
    fadeOutFader: Fader | null = null
    displayCallback: (() => void) | null = null

    // The strip spans the full (fixed) viewport width, so the gradient texture is built once.
    private gradientTexture: Texture | null = null

    constructor() {
        super()
        this.visible = false
    }

    show = (message: ScreenMessage) => {
        const { text, displayCallback, duration = 1500 } = message
        this.displayCallback = displayCallback || null

        // Clear any existing animation
        if (this.fadeInFader) {
            this.fadeInFader.stop(true)
            this.fadeInFader = null
        }
        if (this.fadeOutFader) {
            this.fadeOutFader.stop(true)
            this.fadeOutFader = null
        }

        // Clear previous content
        this.removeChildren()

        // Use the actual game screen dimensions (the visible viewport is wider than the 640 play area).
        const gameScreenWidth = ScreenDimensions.w + TileSize * 4.7
        const gameScreenHeight = ScreenDimensions.h

        // The giant hero text (retained size)
        this.heroText = new TextRenderer().variant("GiganticFont").color(0xffffff).align("center").render(text) as Text
        this.heroText.anchor.set(0, 0)
        this.heroText.x = gameScreenWidth / 2 - this.heroText.width / 2
        this.heroText.y = gameScreenHeight / 2 - this.heroText.height / 2 + VERTICAL_OFFSET

        // Full-width gradient strip, vertically centered on the hero text.
        const stripHeight = this.heroText.height + VERTICAL_PADDING * 2
        const stripTop = this.heroText.y - VERTICAL_PADDING
        if (!this.gradientTexture) {
            this.gradientTexture = buildAmbientStripTexture(gameScreenWidth, stripHeight, STRIP_ALPHA)
        }
        const background = new Sprite(this.gradientTexture)
        background.x = 0
        background.y = stripTop
        background.width = gameScreenWidth
        background.height = stripHeight
        if (this.gradientTexture === Texture.WHITE) {
            // Fallback (no 2D canvas): tint + alpha the flat texture so it still reads as a dark strip.
            background.tint = 0x000000
            background.alpha = STRIP_ALPHA
        }
        this.background = background

        // Background first so the text sits on top of it.
        this.addChild(background)
        this.addChild(this.heroText)

        this.visible = true
        this.alpha = 0.0 // start transparent, fade in

        // Fade in -> hold -> fade out. No pop, no shrink.
        const displayDuration = duration || 2000
        const fadeInDuration = 250 // 250ms fade in time
        const fadeOutDuration = 500 // 500ms fade out time
        const displayTime = displayDuration - fadeInDuration - fadeOutDuration

        this.fadeInFader = new Fader(this, {
            direction: "in",
            minAlpha: 1.0,
            fadeInterval: 5,
            fadeAlphaDecrement: 0.02,
            releaseCallback: () => {
                this.fadeInFader = null
                // Start the display timer after fade in completes
                setTimeout(() => {
                    this.startFadeOut()
                }, Math.max(0, displayTime))
            },
        })
        this.fadeInFader.update()
    }

    startFadeOut = () => {
        this.fadeOutFader = new Fader(this, {
            direction: "out",
            minAlpha: 0.0,
            fadeInterval: 20,
            fadeAlphaDecrement: 0.05,
            releaseCallback: () => {
                this.fadeOutFader = null
                this.visible = false
                this.alpha = 1.0 // Reset opacity for next display
                this.removeChildren()

                if (this.displayCallback) {
                    this.displayCallback()
                    this.displayCallback = null
                }
            },
        })
        this.fadeOutFader.update()
    }

    destroy = () => {
        if (this.fadeInFader) {
            this.fadeInFader.stop(true)
            this.fadeInFader = null
        }
        if (this.fadeOutFader) {
            this.fadeOutFader.stop(true)
            this.fadeOutFader = null
        }
        this.removeChildren()
        this.visible = false
        this.alpha = 1.0 // Reset opacity for next display
    }
}
